import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Users, Wrench, CheckCircle2, Activity, BarChart3, TrendingUp, AlertTriangle } from "lucide-react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Cell } from "recharts";

const KpiTile = ({ label, value, icon: Icon, accent, testid }) => (
    <Card className="p-4 relative overflow-hidden" data-testid={testid}>
        <div className="flex items-start justify-between">
            <div>
                <div className="text-xs uppercase tracking-widest text-muted-foreground">{label}</div>
                <div className="text-3xl font-semibold mt-1 font-mono tabular-nums">{value}</div>
            </div>
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${accent || "bg-secondary"}`}>
                <Icon className="w-5 h-5" />
            </div>
        </div>
    </Card>
);

const SatGauge = ({ value, label }) => {
    const pct = Math.min(100, Math.max(0, ((value || 0) / 3) * 100));
    return (
        <div className="text-center">
            <div className="relative w-24 h-24 mx-auto">
                <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
                    <circle cx="50" cy="50" r="42" stroke="hsl(var(--border))" strokeWidth="10" fill="none" />
                    <circle cx="50" cy="50" r="42" stroke="hsl(var(--accent))" strokeWidth="10" fill="none"
                            strokeDasharray={`${(2*Math.PI*42*pct)/100} 999`} strokeLinecap="round" />
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <div className="text-xl font-semibold font-mono">{value ? value.toFixed(1) : "—"}</div>
                    <div className="text-[10px] uppercase text-muted-foreground">/ 3</div>
                </div>
            </div>
            <div className="text-xs text-muted-foreground mt-1">{label}</div>
        </div>
    );
};

export default function AdminDashboard() {
    const [data, setData] = useState(null);
    const [alertsSummary, setAlertsSummary] = useState(null);

    useEffect(() => {
        api.get("/admin/dashboard").then((r) => setData(r.data));
        api.get("/admin/alerts/summary").then((r) => setAlertsSummary(r.data)).catch(() => setAlertsSummary(null));
    }, []);

    if (!data) return <div className="text-muted-foreground">Cargando KPIs...</div>;

    const statusData = [
        { name: "Pendiente", count: data.pending_today, color: "#D6D6D6" },
        { name: "En progreso", count: data.in_progress_today, color: "#FFD62B" },
        { name: "Completado", count: data.completed_today, color: "#16A34A" },
        { name: "No realizado", count: data.not_done_today, color: "#FF0000" },
    ];

    const s = data.satisfaction || {};

    return (
        <div data-testid="admin-dashboard">
            <h1 className="text-2xl font-semibold mb-4" style={{ fontFamily: "'Space Grotesk',sans-serif" }}>Resumen operacional</h1>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5 mb-4">
                <KpiTile label="Servicios hoy" value={data.services_today} icon={Activity} accent="bg-[hsl(var(--accent))]/20" testid="admin-kpi-services-today" />
                <KpiTile label="% Completado" value={`${Math.round(data.completion_rate_today || 0)}%`} icon={CheckCircle2} accent="bg-green-100" testid="admin-kpi-completion-rate" />
                <KpiTile label="Satisfacción prom." value={s.count ? ((s.comments + s.punctuality + s.friendliness + s.cordiality + s.efficacy) / 5).toFixed(1) : "—"} icon={TrendingUp} accent="bg-[#FFD62B]/30" testid="admin-kpi-satisfaction-avg" />
                <KpiTile label="Técnicos activos" value={data.total_technicians} icon={Wrench} accent="bg-secondary" testid="admin-kpi-active-techs" />
                <Link to="/admin/alerts" className="block" data-testid="admin-kpi-alerts-link">
                    <Card className={`p-4 relative overflow-hidden h-full transition-colors ${(alertsSummary?.open_total || 0) > 0 ? "border-[#FF0000]/30 bg-[#FF0000]/5 hover:bg-[#FF0000]/10" : "hover:bg-secondary/40"}`} data-testid="admin-kpi-alerts">
                        <div className="flex items-start justify-between">
                            <div>
                                <div className="text-xs uppercase tracking-widest text-muted-foreground">Alertas pendientes</div>
                                <div className="text-3xl font-semibold mt-1 font-mono tabular-nums">{alertsSummary?.open_total || 0}</div>
                                <div className="text-[11px] text-muted-foreground mt-0.5">
                                    {alertsSummary
                                        ? `${alertsSummary.device_action_open || 0} dispositivo · ${alertsSummary.mispriced_open || 0} precio`
                                        : "Cargando..."}
                                </div>
                            </div>
                            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${(alertsSummary?.open_total || 0) > 0 ? "bg-[#FF0000]/15" : "bg-secondary"}`}>
                                <AlertTriangle className={`w-5 h-5 ${(alertsSummary?.open_total || 0) > 0 ? "text-[#FF0000]" : ""}`} />
                            </div>
                        </div>
                    </Card>
                </Link>
            </div>

            <div className="grid gap-4 lg:grid-cols-3">
                <Card className="lg:col-span-2 p-4">
                    <h2 className="font-semibold mb-3 flex items-center gap-2"><BarChart3 className="w-4 h-4" /> Servicios hoy por estado</h2>
                    <div className="h-60">
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={statusData} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E2E8F0" />
                                <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                                <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                                <Tooltip />
                                <Bar dataKey="count" radius={[6, 6, 0, 0]}>
                                    {statusData.map((d, i) => <Cell key={i} fill={d.color} />)}
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </Card>
                <Card className="p-4">
                    <h2 className="font-semibold mb-3">Satisfacción (semana)</h2>
                    {s.count ? (
                        <div className="grid grid-cols-3 gap-3">
                            <SatGauge value={s.comments} label="Comentarios" />
                            <SatGauge value={s.punctuality} label="Puntualidad" />
                            <SatGauge value={s.friendliness} label="Amabilidad" />
                            <SatGauge value={s.cordiality} label="Cordialidad" />
                            <SatGauge value={s.efficacy} label="Eficacia" />
                            <div className="flex flex-col justify-center items-center">
                                <div className="text-2xl font-mono">{s.count}</div>
                                <div className="text-xs text-muted-foreground">encuestas</div>
                            </div>
                        </div>
                    ) : <div className="text-muted-foreground text-sm">Sin datos aún</div>}
                </Card>
            </div>

            <Card className="p-4 mt-4" data-testid="admin-tech-leaderboard">
                <h2 className="font-semibold mb-3 flex items-center gap-2"><Users className="w-4 h-4" /> Técnicos top (semana)</h2>
                {(data.top_technicians || []).length === 0 ? (
                    <div className="text-muted-foreground text-sm">Sin reportes esta semana</div>
                ) : (
                    <ul className="space-y-1.5">
                        {data.top_technicians.map((t, i) => (
                            <li key={t.code} className="flex items-center gap-3 py-1.5">
                                <div className="w-6 text-center font-mono text-muted-foreground">{i + 1}</div>
                                <Avatar className="w-8 h-8"><AvatarFallback className="text-xs bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))]">{t.full_name.split(" ").map(s=>s[0]).slice(0,2).join("")}</AvatarFallback></Avatar>
                                <div className="flex-1 min-w-0"><div className="font-medium truncate">{t.full_name}</div><div className="text-xs text-muted-foreground font-mono">{t.code}</div></div>
                                <Badge variant="outline" className="font-mono">{t.reports} reportes</Badge>
                            </li>
                        ))}
                    </ul>
                )}
            </Card>

            <Card className="p-4 mt-4" data-testid="admin-mispriced-panel">
                <div className="flex items-center justify-between mb-3">
                    <h2 className="font-semibold flex items-center gap-2">
                        <AlertTriangle className={`w-4 h-4 ${(data.mispriced_alerts || []).length > 0 ? "text-[#FF0000]" : ""}`} />
                        Alertas de precio reportadas por técnicos
                    </h2>
                    <Link to="/admin/alerts" className="text-xs text-blue-700 hover:underline" data-testid="admin-mispriced-see-all">
                        Ver todas las alertas →
                    </Link>
                </div>
                <p className="text-xs text-muted-foreground mb-3">Servicios en los que el técnico indicó que el precio podría estar mal cobrado. Esta información es interna y no se muestra al cliente.</p>
                {(data.mispriced_alerts || []).length === 0 ? (
                    <div className="text-sm text-muted-foreground py-6 text-center border border-dashed rounded-lg" data-testid="admin-mispriced-empty">
                        Sin alertas pendientes
                    </div>
                ) : (
                    <ul className="divide-y border rounded-lg" data-testid="admin-mispriced-list">
                        {data.mispriced_alerts.map((a) => (
                            <li key={a.report_id} className="px-3 py-2.5 flex items-start gap-3" data-testid={`admin-mispriced-row-${a.report_id}`}>
                                <span className="mt-1.5 inline-block w-2 h-2 rounded-full bg-[#FF0000] shrink-0" />
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <span className="font-medium text-sm truncate">{a.client_name}</span>
                                        {a.invoice_number && <Badge variant="outline" className="font-mono text-[10px]">{a.invoice_number}</Badge>}
                                    </div>
                                    <div className="text-xs text-muted-foreground mt-0.5">
                                        <span className="font-mono">{a.service_date}</span>
                                        {" · "}
                                        <span>{a.technician_name} ({a.technician_code})</span>
                                        {a.payment_type && <> {" · "} <span className="uppercase">{a.payment_type}</span></>}
                                    </div>
                                </div>
                            </li>
                        ))}
                    </ul>
                )}
            </Card>
        </div>
    );
}
